import React from 'react';
import { Link } from 'react-router-dom';
import { CONTACT_EMAIL, MAILTO_PLAIN } from '../lib/site';
import PageHeader from '../components/ui/PageHeader';
import Tabs from '../components/ui/Tabs';
import Accordion, { type AccordionItem } from '../components/ui/Accordion';

const a = 'text-primary-300 hover:text-primary-200 underline underline-offset-4';

const ESTIMATES: AccordionItem[] = [
  {
    id: 'quote',
    title: 'Is the estimate a quote?',
    content: (
      <p>
        No. It is a nonbinding preview calculated in your browser — nothing is submitted, saved, or tied to you. A final quote is staff-reviewed after you send an inquiry.
      </p>
    ),
  },
  {
    id: 'approved',
    title: 'Which numbers are approved, and which are placeholders?',
    content: (
      <p>
        Approved: the tier price ranges and starting prices, the 0.75× and 1.00×+ management-interface multipliers, the $2,000 AI self-healing price, and the 17 hosting base rates.
        Illustrative placeholders: where a project lands inside the T1–T3 ranges, tier thresholds, the high-traffic cutoff, and the hosting risk allowances.
      </p>
    ),
  },
  {
    id: 'tiers',
    title: 'What do T1–T5, T3A and T3B mean?',
    content: (
      <p>
        See the <Link to="/tiers" className={a}>tier reference</Link>. T3A is presentation-focused (we build the experience); T3B is integration-focused (we connect existing services). Both are $1,500–$5,000, and a project that needs both is one Tier 3 commission.
      </p>
    ),
  },
  {
    id: 't3b',
    title: 'Does using an outside service make my site T3B?',
    content: (
      <p>
        Not automatically. Simple links or embeds can stay T1–T2; substantial integration of an existing service is T3B. A custom server, credentials, or webhooks mean T4A; stored orders, accounts, or a database mean T4B. Infrastructure and security requirements can raise the final classification.
      </p>
    ),
  },
  {
    id: 'locked',
    title: 'Why can’t I add an advanced interface or AI self-healing?',
    content: (
      <p>
        Both require T4 or higher; AI self-healing also requires FairCorp hosting and is still in development. Below T4, the standard content-management interface (0.75×) is available.
      </p>
    ),
  },
];

const HOSTING: AccordionItem[] = [
  {
    id: 'need',
    title: 'Do I need FairCorp hosting if FairCorp builds my site?',
    content: <p>No. Development and hosting are separate purchases: host elsewhere, or leave hosting later without forfeiting your site.</p>,
  },
  {
    id: 'external',
    title: 'Can you host a site someone else built?',
    content: (
      <p>
        Yes. Choose the category that matches what you run in the <Link to="/plans" className={a}>plan finder</Link> and send a hosting inquiry; staff confirm sizing.
      </p>
    ),
  },
  {
    id: 'choose',
    title: 'How is my plan chosen?',
    content: (
      <p>
        By category first, then by options such as a content-management interface or high traffic. The <Link to="/plans" className={a}>hosting plans page</Link> shows the result and all 17 plans.
      </p>
    ),
  },
  {
    id: 'leave',
    title: 'What happens to my data if I leave?',
    content: <p>Your commissioned source code and accumulated data are yours to transfer. Eligible recovery backups are kept up to six months after hosting ends — a transfer window, not extended hosting.</p>,
  },
  {
    id: 'sla',
    title: 'Is there an uptime guarantee?',
    content: <p>This site does not publish uptime, support-hours, or recovery-time commitments. Ask in your hosting inquiry.</p>,
  },
];

const POLICY: AccordionItem[] = [
  {
    id: 'who',
    title: 'Who is the contracting party?',
    content: (
      <p>
        Brennan Faires, operating as FairCorp. See the <Link to="/tos" className={a}>Terms of Service</Link> and <Link to="/privacy" className={a}>Privacy Policy</Link> for the full text.
      </p>
    ),
  },
  {
    id: 'minor',
    title: 'Are small changes free?',
    content: <p>Small text, content, hours, image, and styling fixes are free at every tier, with no hosting purchase required. Major new work is quoted individually.</p>,
  },
  {
    id: 'own',
    title: 'Who owns the work?',
    content: <p>You do. Commissioned source code and accumulated data are yours to transfer; sensitive exports travel by hand-delivered physical media.</p>,
  },
  {
    id: 'data',
    title: 'What does this site collect?',
    content: (
      <p>
        Some pages record an aggregate page-view count so directory listings stay accurate — statistics, not advertising profiles. The quote and plan tools run entirely in your browser. Details: <Link to="/privacy" className={a}>Privacy Policy</Link>.
      </p>
    ),
  },
];

const SITES: AccordionItem[] = [
  {
    id: 'listed',
    title: 'How do I get a site listed or hosted?',
    content: (
      <p>
        Send a <Link to="/contact?path=hosting" className={a}>hosting inquiry</Link>. The <Link to="/sites" className={a}>directory</Link> shows live and upcoming sites.
      </p>
    ),
  },
  {
    id: 'visits',
    title: 'What do the visit counts mean?',
    content: <p>They are aggregate page-view statistics that keep directory listings accurate — not visitor profiles.</p>,
  },
];

const Support: React.FC = () => (
  <div className="page-inner pb-10">
    <PageHeader title="Support" lead="Short answers about estimates, hosting, and policy. Anything missing goes to the inbox below." />

    <Tabs
      label="Support topics"
      tabs={[
        { id: 'estimates', label: 'Quotes & estimates', panel: <Accordion level={2} items={ESTIMATES} defaultOpen={['quote']} /> },
        { id: 'hosting', label: 'Hosting', panel: <Accordion level={2} items={HOSTING} defaultOpen={['need']} /> },
        { id: 'policy', label: 'Contracts & policy', panel: <Accordion level={2} items={POLICY} defaultOpen={['who']} /> },
        { id: 'sites', label: 'Hosted sites', panel: <Accordion level={2} items={SITES} defaultOpen={['listed']} /> },
      ]}
    />

    <div className="panel mt-6 p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
      <p className="text-sm text-secondary-200">
        Still stuck? Email{' '}
        <a href={MAILTO_PLAIN} className={`${a} font-semibold break-all`}>
          {CONTACT_EMAIL}
        </a>
        .
      </p>
      <Link to="/contact" className="btn btn-primary">
        Contact options
      </Link>
    </div>
  </div>
);

export default Support;
